import { useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import SnackbarContent from '@mui/material/SnackbarContent'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import CheckCircle from '@mui/icons-material/CheckCircle'
import CloseIcon from '@mui/icons-material/Close'
import ErrorIcon from '@mui/icons-material/Error'
import { themeConfig } from '@gap/shared/theme'
import { snackbarSurfaceSx } from 'reusable/mui/snackbar/snackbarSurface'
import { computeBarPlacement, type ContentEnd } from './markdownLoaderPlacement'

/** What the owner of the generating document reports, driving the bar, the lock and the fade. */
export type MarkdownLoaderState = 'loading' | 'complete' | 'error' | 'idle'

/** Put on the markdown wrapper while `loaderState` is `loading`, to drive the feather fade. */
export const MARKDOWN_LOADING_CLASS = 'markdown-loading'

export interface MarkdownLoaderStatusProps {
   label: string | null | undefined
   state: MarkdownLoaderState
   /** Dismisses the bar. Only wired up for `error`, since the other states end when the work does. */
   onDismiss?: () => void
}

/** Cross-fade for a label swapped in place, so the sequence reads as progress, not a jump cut. */
const MESSAGE_FADE_MS = 500

/** Fraction of the remaining distance closed each frame while following growing content. */
const FOLLOW_EASING = 0.15
/** Below this, snap rather than animate an imperceptible sliver forever. */
const SETTLE_THRESHOLD_PX = 0.5

const isScrollable = (element: Element) => {
   const { overflowY } = getComputedStyle(element)
   return (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay')
      && element.scrollHeight > element.clientHeight
}

/**
 * Finds the bottom edge of whichever ancestor actually scrolls.
 * @param element Where to start walking up from.
 * @returns That ancestor's viewport bottom, or the window's when nothing scrolls.
 */
const findScrollportBottom = (element: Element | null): number => {
   if (!element) return window.innerHeight
   if (isScrollable(element)) return element.getBoundingClientRect().bottom
   return findScrollportBottom(element.parentElement)
}

/**
 * Finds where the document's text ends, which is not where the stretched editor box ends.
 * @param anchor The zero-height anchor rendered at the end of the document.
 * @returns The viewport y to hang under and whether there is any text, or undefined with no editor.
 */
const findContentEnd = (anchor: Element): ContentEnd | undefined => {
   // Not `[contenteditable="true"]`: MDXEditor flips the attribute to "false" mid-generation.
   const contentEditable = anchor.parentElement?.querySelector('.mdxeditor-root-contenteditable [contenteditable]')
   if (!contentEditable) return undefined

   const lastBlock = contentEditable.lastElementChild
   if (!lastBlock || !contentEditable.textContent?.trim()) {
      return { y: contentEditable.getBoundingClientRect().top, hasContent: false }
   }
   return { y: lastBlock.getBoundingClientRect().bottom, hasContent: true }
}

/**
 * Measures every frame and eases the bar to wherever `computeBarPlacement` says it belongs.
 * @param params The zero-height anchor to position against, the bar itself, and whether to run.
 */
function useBarPlacement(params: { anchorRef: React.RefObject<HTMLElement | null>, barRef: React.RefObject<HTMLElement | null>, active: boolean }) {
   const { anchorRef, barRef, active } = params
   const renderedTopRef = useRef<number | null>(null)

   useEffect(() => {
      if (!active) {
         renderedTopRef.current = null
         return
      }

      let frameId: number

      const step = () => {
         const anchor = anchorRef.current
         const bar = barRef.current
         if (anchor && bar) {
            const { top: targetTop, isPinned } = computeBarPlacement({
               anchorTop: anchor.getBoundingClientRect().top,
               contentEnd: findContentEnd(anchor),
               scrollportBottom: findScrollportBottom(anchor.parentElement),
               barHeight: bar.getBoundingClientRect().height,
            })

            const renderedTop = renderedTopRef.current
            // Snapped on the first frame too, so becoming visible doesn't animate in from top: 0.
            const shouldSnap = renderedTop === null || isPinned || Math.abs(targetTop - renderedTop) < SETTLE_THRESHOLD_PX
            renderedTopRef.current = shouldSnap ? targetTop : renderedTop + (targetTop - renderedTop) * FOLLOW_EASING

            bar.style.top = `${renderedTopRef.current}px`
         }
         frameId = requestAnimationFrame(step)
      }

      frameId = requestAnimationFrame(step)
      return () => cancelAnimationFrame(frameId)
   }, [anchorRef, barRef, active])
}

/**
 * A status bar that follows the end of the document, saying what the AI agent is doing.
 * @param props The label to show, the loader state, and the dismiss handler for `error`.
 */
export default function MarkdownLoaderStatus({ label, state, onDismiss }: MarkdownLoaderStatusProps) {
   const visible = state !== 'idle'
   const isError = state === 'error'
   const anchorRef = useRef<HTMLDivElement>(null)
   const barRef = useRef<HTMLDivElement>(null)
   const theme = useTheme()
   // Keeps the last real label on screen while the bar fades out.
   const [displayLabel, setDisplayLabel] = useState(label)

   useEffect(() => {
      if (label) setDisplayLabel(label)
   }, [label])

   useBarPlacement({ anchorRef, barRef, active: visible })

   return (
      // Zero-height anchor, so the bar gets a containing block without costing layout height.
      <Box ref={anchorRef} sx={{ position: 'relative', height: 0 }}>
         <Box
            ref={barRef}
            sx={{
               position: 'absolute',
               left: 0,
               right: 0,
               zIndex: 10,
               // No `top` transition: `useBarPlacement` writes it every frame and eases it itself.
            }}
         >
            <SnackbarContent
               aria-hidden={!visible}
               message={<>
                  {/* Filled, not Outlined: the design system reserves Filled for status indicators. */}
                  {state === 'complete'
                     ? <CheckCircle sx={{ fontSize: 20, color: themeConfig.system.success[600] }} />
                     : isError
                        ? <ErrorIcon sx={{ fontSize: 20, color: themeConfig.system.error[600] }} />
                        : <CircularProgress size={20} thickness={4} sx={{ color: themeConfig.primary[400] }} />}
                  <Typography
                     // Remounting on a new label is what replays the fade.
                     key={displayLabel}
                     variant="body2"
                     component="span"
                     sx={{
                        '@keyframes markdownLoaderMessageIn': { from: { opacity: 0 }, to: { opacity: 1 } },
                        animation: `markdownLoaderMessageIn ${MESSAGE_FADE_MS}ms ease-out`,
                     }}
                  >
                     {displayLabel ?? ''}
                  </Typography>
               </>}
               action={isError && onDismiss
                  ? <IconButton size="small" aria-label="Close" onClick={onDismiss} sx={{ color: themeConfig.primary[900] }}>
                     <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  : null}
               sx={[snackbarSurfaceSx({
                  dark: theme.palette.mode === 'dark',
                  paperColor: theme.palette.background.paper,
                  shadow: theme.shadows[8],
               }), {
                  width: '100%',
                  whiteSpace: 'nowrap',
                  opacity: visible ? 1 : 0,
                  // Inert unless there's a close button, since the bar overlays the document.
                  pointerEvents: isError && onDismiss ? 'auto' : 'none',
                  transform: visible ? 'translateY(0)' : 'translateY(12px)',
                  transition: 'opacity 0.25s ease, transform 0.25s ease',
               }]}
            />
         </Box>
      </Box>
   )
}
